import { useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { RiDeleteBinFill } from "react-icons/ri";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import { Paragraph } from "../../../components/Paragraph";
import { Loader } from "../../../components/Loader";
import { Card } from "../../../components/Card";
import { H3 } from "../../../components/Heading";
import { Button } from "../../../components/Button";
import { Image } from "../../../components/Image";
import { useMandateCheckList } from "../../../data/useMandateCheckList";
import {
  CHECK_ICONS_LARGE,
  screeningWizardCheckNames,
  selectionCoversAdverseMedia,
  selectionCoversPepAndSanctions,
  selectionCoversSanctions,
} from "../../../utils/screeningChecks";
import { ROUTE } from "../../../routes";
import { NewProfileScreeningFrame } from "./NewProfileScreeningFrame";

const checksIcons: Record<string, any> = CHECK_ICONS_LARGE;

export const SelectChecksStep = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const outlet = useOutletContext<any>();
  const profile = outlet?.profile;

  const isEntity = profile?._type === "entity";
  const mode = isEntity ? "entity" : "person";

  const [selectedChecks, setSelectedChecks] = useState<any[]>(
    (location.state as any)?.selectedChecks ?? [],
  );

  // Both generations are requested; the server returns only the checks this
  // tenant's screening provider allows.
  const checkNames = useMemo(
    () => screeningWizardCheckNames(isEntity ? "entity" : "individual"),
    [isEntity],
  );

  const { data, isLoading } = useMandateCheckList(false, checkNames);

  const checks = data?.data ?? [];

  const handleRemove = (checkId: number) => {
    setSelectedChecks((prev) => prev.filter((c) => c.id !== checkId));
  };

  const handleAdd = (checkId: number) => {
    setSelectedChecks((prev) => {
      const checkToAdd = checks.find((c: any) => c.id === checkId);
      if (!checkToAdd) return prev;
      if (prev.some((c) => c.id === checkId)) return prev;
      return [...prev, checkToAdd];
    });
  };

  const handleNext = () => {
    const checkpointIds = selectedChecks.map((c) => c.id);

    const selectedNames = selectedChecks.map((c) => c.name);

    const isAdverseMediaSelected = selectionCoversAdverseMedia(selectedNames);

    const isSanctionsSelected = selectionCoversSanctions(selectedNames);

    const isPepAndSanctionsSelected =
      selectionCoversPepAndSanctions(selectedNames);

    const showAddressStep = selectedChecks.some((c) =>
      ["address_verification_check"].includes(c.name),
    );

    const nextRoute = ROUTE.PROFILE_SCREENING_ADD_PERSON;

    navigate(nextRoute.replace(":id", profileId.toString()), {
      state: {
        checkpointIds,
        selectedChecks,
        isAdverseMediaSelected,
        isSanctionsSelected,
        isPepAndSanctionsSelected,
        showAddressStep,
      },
    });
  };

  const CheckItem = ({ check }: { check: any }) => {
    const isAdded = selectedChecks.some((c) => c.id === check.id);
    const icon = checksIcons[check.name];

    return isAdded ? (
      <Card mb={1} p={3}>
        <Flex gap="70" justifyContent="space-between" alignItems="center">
          <Flex gap="16" alignItems="center">
            {icon && (
              <Image src={icon} alt="checks" height="auto" width="50" mr={1} />
            )}
            <Paragraph fontWeight={500}>{check.actual_name}</Paragraph>
          </Flex>

          <Button
            Icon={RiDeleteBinFill}
            variant="grayText"
            onClick={() => handleRemove(check.id)}
          >
            Remove
          </Button>
        </Flex>
      </Card>
    ) : (
      <Box mb={1} p={3}>
        <Flex gap="70" justifyContent="space-between" alignItems="center">
          <Flex gap="16" alignItems="center">
            {icon && (
              <Image src={icon} alt="checks" height="auto" width="50" mr={1} />
            )}
            <Paragraph fontWeight={500}>{check.actual_name}</Paragraph>
          </Flex>

          <Button
            variant="gray"
            Icon={FaPlus}
            iconSize="14px"
            onClick={() => handleAdd(check.id)}
            isDisabled={isAdded}
          >
            Add
          </Button>
        </Flex>
      </Box>
    );
  };

  return (
    <NewProfileScreeningFrame step="selectChecks" mode={mode}>
      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="25vh">
          <Loader />
        </Flex>
      ) : (
        <>
          <Box mb={3} minWidth={500}>
            <H3>Select Checks</H3>
            <Paragraph fontSize={1} mb={3}>
              Please select the checks you would like to perform.
            </Paragraph>

            {checks.map((check: any) => (
              <CheckItem key={check.id} check={check} />
            ))}
          </Box>

          <Flex alignItems="center" justifyContent="flex-end">
            <Button
              mr={2}
              onClick={handleNext}
              isDisabled={selectedChecks.length === 0}
            >
              Next
            </Button>
          </Flex>
        </>
      )}
    </NewProfileScreeningFrame>
  );
};
